<template>
  <div class="airport-form">
    <toolbar
      backRoute="/dashboard/airports"
      :beforeBack="checkForChanges"
      :title="toolbarTitle"
      :deleteButtonLabel="deleteButtonLabel"
      deleteButtonEvent="airport-item-delete"
      saveButtonLabel="Save airport"
      saveButtonEvent="airport-item-save"
      :deleteInProgress="disableInProgress"
      :saveInProgress="saveInProgress" />

    <d-container fluid class="common-form px-0 pt-5 pb-5">
      <d-form>
        <validation-observer
          v-if="!loadError && allDataLoaded"
          ref="airport-form"
          tag="form">
          <d-row>
            <d-col cols="6">
              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label for="name">Name:</label>
                    <d-input
                      v-model="data.name" />
                  </div>

                  <div class="form-group">
                    <label for="uk_county">UK County:</label>
                    <d-input
                      v-model="data.uk_county" />
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <label for="country_id">Country:</label>
                    <div :class="{ 'form-control': true, 'is-select': true }">
                      <v-select
                        :options="countriesOptions"
                        v-model="data.country_id"
                        :reduce="country => country.id"
                        placeholder="Select a country"
                        label="text">
                        <template slot="no-options">
                          There are no countries for the provided phrase&hellip;
                        </template>
                      </v-select>
                    </div>
                  </div>

                  <div class="form-group">
                    <label for="tmezone">Timezone:</label>
                    <div :class="{ 'form-control': true, 'is-select': true }">
                      <v-select
                        :options="timezones"
                        v-model="data.timezone"
                        :reduce="country => country.value"
                        placeholder="Select a timezone"
                        label="text">
                        <template slot="no-options">
                          There are no timezones for the provided phrase&hellip;
                        </template>
                      </v-select>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label for="icao">ICAO:</label>

                    <validation-provider
                      rules="required"
                      name="icao"
                      v-slot="{ errors }">
                      <d-input
                        v-model="data.icao"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <label for="iata">IATA:</label>
                    <d-input
                      v-model="data.iata" />
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label for="hours">Hours:</label>
                <d-input
                  v-model="data.hours" />
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label for="website">Website:</label>

                <d-input-group>
                  <d-input
                    v-model="data.website" />
                  <d-input-group-addon append>
                    <d-btn
                      theme="primary"
                      :disabled="!data.website"
                      @click.prevent="openUrl(data.website)">
                      <i class="material-icons">launch</i>
                    </d-btn>
                  </d-input-group-addon>
                </d-input-group>
              </div>

              <div class="form-group">
                <label for="address">Address:</label>
                <d-input
                  v-model="data.address" />
              </div>

              <div class="form-group">
                <d-checkbox
                  inline
                  v-model="data.slot_required"
                  toggle
                  toggle-small>
                  Slot required
                </d-checkbox>
              </div>

              <strong
                v-if="data.slot_required"
                class="pb-2 d-block">
                Arrival slot tolerance:
              </strong>

              <d-row v-if="data.slot_required">
                <d-col cols="6">
                  <div class="form-group">
                    <validation-provider
                      rules="required"
                      name="arrival_slot_tolerance_before"
                      v-slot="{ errors }">
                      <d-input-group
                        prepend="Before (-)"
                        append="min"
                        :class="errors.length ? 'is-invalid' : null">
                        <d-input
                          v-model="data.arrival_slot_tolerance_before"
                          type="number"
                          step="1"
                          class="text-right"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <validation-provider
                      rules="required"
                      name="arrival_slot_tolerance_after"
                      v-slot="{ errors }">
                      <d-input-group
                        prepend="After (+)"
                        append="min"
                        :class="errors.length ? 'is-invalid' : null">
                        <d-input
                          v-model="data.arrival_slot_tolerance_after"
                          type="number"
                          step="1"
                          class="text-right"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <strong
                v-if="data.slot_required"
                class="pb-2 d-block">
                Departure slot tolerance:
              </strong>

              <d-row v-if="data.slot_required">
                <d-col cols="6">
                  <div class="form-group">
                    <validation-provider
                      rules="required"
                      name="departure_slot_tolerance_before"
                      v-slot="{ errors }">
                      <d-input-group
                        prepend="Before (-)"
                        append="min"
                        :class="errors.length ? 'is-invalid' : null">
                        <d-input
                          v-model="data.departure_slot_tolerance_before"
                          type="number"
                          min="0"
                          step="1"
                          class="text-right"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <validation-provider
                      rules="required"
                      name="departure_slot_tolerance_after"
                      v-slot="{ errors }">
                      <d-input-group
                        prepend="After (+)"
                        append="min"
                        :class="errors.length ? 'is-invalid' : null">
                        <d-input
                          v-model="data.departure_slot_tolerance_after"
                          type="number"
                          min="0"
                          step="1"
                          class="text-right"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
            </d-col>
          </d-row>

          <d-row class="extended-row mt-4">
            <d-col cols="6">
              <h5>Catering</h5>

              <table class="table table-sm table-special">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Phone</th>
                    <th>Email(s)</th>
                    <th>Website</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(rowIndex, index) of [0, 1, 2, 3]"
                    :key="'categring-row-' + index">
                    <td class="p-0">
                      <d-input
                        type="text"
                        v-model="data.caterings[rowIndex]['company']" />
                    </td>
                    <td class="p-0">
                      <d-input
                        type="text"
                        v-model="data.caterings[rowIndex]['phone']" />
                    </td>
                    <td class="p-0">
                      <d-input
                        type="text"
                        v-model="data.caterings[rowIndex]['email']" />
                    </td>
                    <td class="p-0">
                      <d-input
                        type="text"
                        v-model="data.caterings[rowIndex]['website']" />
                    </td>
                    <td class="p-0">
                      <d-input
                        type="text"
                        v-model="data.caterings[rowIndex]['notes']" />
                    </td>
                    <td class="p-1 special-bg text-primary">
                      <i class="material-icons" @click="copyRow('catering', rowIndex)"><template v-if="cateringCopied === rowIndex">check</template><template v-else>content_copy</template></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-col>
            <d-col cols="6">
              <h5>Transport</h5>

              <table class="table table-sm table-special">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Phone</th>
                    <th>Email(s)</th>
                    <th>Website</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(rowIndex, index) of [0, 1, 2, 3]"
                    :key="'transport-row-' + index">
                    <td class="p-0">
                      <d-input
                        type="text"
                        v-model="data.transports[rowIndex]['company']" />
                    </td>
                    <td class="p-0">
                      <d-input
                        type="text"
                        v-model="data.transports[rowIndex]['phone']" />
                    </td>
                    <td class="p-0">
                      <d-input
                        type="text"
                        v-model="data.transports[rowIndex]['email']" />
                    </td>
                    <td class="p-0">
                      <d-input
                        type="text"
                        v-model="data.transports[rowIndex]['website']" />
                    </td>
                    <td class="p-0">
                      <d-input
                        type="text"
                        v-model="data.transports[rowIndex]['notes']" />
                    </td>
                    <td class="p-1 special-bg text-primary">
                      <i class="material-icons" @click="copyRow('transport', rowIndex)"><template v-if="transportCopied === rowIndex">check</template><template v-else>content_copy</template></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-col>
          </d-row>
        </validation-observer>

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          Loading data&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          The app were unable to load data. <a href="javascript:window.location.reload();" class="alert-link">Refresh the app</a> to try again.
        </d-alert>
      </d-form>

      <warnings-list
        v-if="loadedData"
        referenceType="AIRPORT"
        :referenceId="data.id"
        itemName="airport" />

      <handlers-list
        v-if="loadedData"
        title="Handlers"
        referenceType="AIRPORT"
        :referenceId="data.id"
        referenceIdKey="airport_id"
        itemName="airport"
        popupItemType="handler" />

      <checklist-list
        v-if="loadedData"
        referenceType="AIRPORT"
        :referenceId="data.id"
        itemName="airport" />

      <div class="form-group">
        <label for="notes">Notes:</label>
        <d-textarea
          v-model="data.notes"
          :value.prop="data.notes"
          :rows="18" />
      </div>
    </d-container>
  </div>
</template>

<script>
import ChecklistList from './../form-parts/CheckLists.vue';
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';
import HandlersList from './../form-parts/Handlers.vue';
import HasChangesDetection from './../../mixins/HasChangesDetection.vue';
import HasUrlField from './../../mixins/HasUrlField.vue';
import ListUtils from './../../utils/ListUtils.js';
import TimezonesData from './../../data/timezones.js';
import WarningsList from './../form-parts/Warnings.vue';

export default {
  name: 'airport-form',
  mixins: [
    HasChangesDetection,
    HasUrlField
  ],
  components: {
    'checklist-list': ChecklistList,
    'handlers-list': HandlersList,
    'warnings-list': WarningsList
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedCountries;
    },
    isEdit () {
      return this.$route.params.id !== 'new' && parseInt(this.$route.params.id, 10) > 0;
    },
    toolbarTitle () {
      if (!this.isEdit) {
        return 'Add new airport';
      }

      return 'Edit airport';
    },
    deleteButtonLabel () {
      if (this.$route.params.id === 'new') {
        return false;
      }

      return 'Disable airport';
    },
    countriesOptions () {
      return CommonGetters.getCountriesForSelect(this.countries);
    }
  },
  data () {
    return {
      data: {
        id: 0,
        name: '',
        country_id: null,
        uk_county: '',
        timezone: null,
        icao: '',
        iata: '',
        hours: '',
        website: '',
        address: '',
        notes: '',
        slot_required: false,
        arrival_slot_tolerance_before: '',
        arrival_slot_tolerance_after: '',
        departure_slot_tolerance_before: '',
        departure_slot_tolerance_after: '',
        // eslint-disable-next-line
        caterings: [{ company: '', phone: '', email: '', website: '', notes: '' }, { company: '', phone: '', email: '', website: '', notes: '' }, { company: '', phone: '', email: '', website: '', notes: '' }, { company: '', phone: '', email: '', website: '', notes: '' }],
        // eslint-disable-next-line
        transports: [{ company: '', phone: '', email: '', website: '', notes: '' }, { company: '', phone: '', email: '', website: '', notes: '' }, { company: '', phone: '', email: '', website: '', notes: '' }, { company: '', phone: '', email: '', website: '', notes: '' }]
      },
      timezones: [],
      loadError: false,
      loadedData: false,
      countries: [],
      loadedCountries: false,
      disableInProgress: false,
      saveInProgress: false,
      cateringCopied: false,
      cateringCopiedTimeout: false,
      transportCopied: false,
      transportCopiedTimeout: false
    };
  },
  mounted () {
    if (!this.isEdit) {
      this.loadedData = true;
    } else {
      this.loadData();
    }

    this.timezones = TimezonesData;
    this.loadAdditionalData();
    this.$bus.$on('airport-item-delete', this.disableAirport);
    this.$bus.$on('airport-item-save', this.validateForm);
  },
  methods: {
    loadData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/airports/item/',
        instanceFields: ['id', 'name', 'country_id', 'timezone', 'uk_county', 'icao', 'iata', 'hours', 'website', 'address', 'notes', 'slot_required', 'arrival_slot_tolerance_before', 'arrival_slot_tolerance_after', 'departure_slot_tolerance_before', 'departure_slot_tolerance_after', 'caterings', 'transports'],
        booleanFields: ['slot_required'],
        loadedKey: 'loadedData'
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/countries/items',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });
    },
    disableAirport () {
      this.disableInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/airports/disable/',
        id: parseInt(this.$route.params.id, 10),
        successTitle: 'Airport disabled',
        successText: 'The selected airport has been disabled',
        confirmTitle: 'Do you really want to disable this airport?',
        confirmText: 'This airport will be no longer displayed on the airports lists',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        buttonConfirmText: 'Yes, disable',
        successAction: () => {
          this.disableInProgress = false;
          this.$router.push('/dashboard/airports');
          this.$bus.$emit('calendar-reload-airports');
        },
        cancelAction: () => {
          this.disableInProgress = false;
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['airport-form'], this.submitForm);
    },
    submitForm () {
      let endpointUrl = '/airports/store';
      let mode = 'add';

      if (this.id > 0) {
        endpointUrl = '/airports/update/' + parseInt(this.id, 10);
        mode = 'edit';
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: this.data,
        successTitle: mode === 'add' ? 'Airport added' : 'Airport saved',
        successText: 'The airport has been successfully saved!',
        errorTitle: 'An error occurred!',
        errorText: 'The operation not finished successfully. Please try again.',
        successAction: () => {
          this.$router.push('/dashboard/airports');
          this.$bus.$emit('calendar-reload-airports');
        }
      });
    },
    copyRow (type, index) {
      let dataToCopy = '';

      if (type !== 'catering' && type !== 'transport') {
        return;
      }

      if (type === 'catering') {
        dataToCopy = [
          this.data.caterings[index].company,
          this.data.caterings[index].phone,
          this.data.caterings[index].email,
          this.data.caterings[index].website,
          this.data.caterings[index].notes
        // eslint-disable-next-line
        ].join("\n");
      } else if (type === 'transport') {
        dataToCopy = [
          this.data.transports[index].company,
          this.data.transports[index].phone,
          this.data.transports[index].email,
          this.data.transports[index].website,
          this.data.transports[index].notes
        // eslint-disable-next-line
        ].join("\n");
      }

      navigator.clipboard.writeText(dataToCopy).then(() => {
        this[type + 'Copied'] = index;
        clearTimeout(this[type + 'CopiedTimeout']);

        this[type + 'CopiedTimeout'] = setTimeout(() => {
          this[type + 'Copied'] = false;
        }, 2000);
      }, () => {
        // eslint-disable-next-line
        alert('Unable to copy selected data to clipboard');
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('airport-item-delete', this.disableAirport);
    this.$bus.$off('airport-item-save', this.validateForm);
  }
}
</script>

<style lang="scss" scoped>
.table-special thead th {
  background: #f0f0f0;
}

.table-special /deep/ input {
  border-radius: 0;
  font-size: 10px;
  padding: 0.5rem 0.25rem;
}

.table-special .special-bg {
  background: #f0f0f0;

  i {
    cursor: pointer;
  }
}

.extended-row {
  margin-left: -150px;
  margin-right: -150px;
}

@media (max-width: 1480px) {
  .extended-row {
    margin-left: -100px;
    margin-right: -100px;
  }
}

@media (max-width: 1380px) {
  .extended-row {
    margin-left: -50px;
    margin-right: -50px;
  }
}

@media (max-width: 1280px) {
  .extended-row {
    margin-left: -15px;
    margin-right: -15px;
  }
}
</style>
